import React, { useRef, useState } from "react";
import { deleteProgramImage, uploadProgramImage } from "../programsApi";
import { ProgramImage } from "../types";

// Every image (core slots and the general gallery uploader below) is capped
// at 200KB -- keeps the site's payload small since these are served straight
// to visitors on first paint. Matches the backend's own MAX_IMAGE_BYTES in
// upload.service.ts; enforced here too for instant feedback before upload.
const MAX_IMAGE_BYTES = 200 * 1024;

// Every image uploaded via the general uploader below the core slots is
// just gallery content -- there's no separate purpose to pick.
const GALLERY_ROLE: ProgramImage["role"] = "gallery";

const RASTER_ACCEPT = "image/jpeg,image/png,image/webp";

const CORE_SLOTS: { role: ProgramImage["role"]; label: string; hint: string; accept: string }[] = [
  { role: "hero", label: "核心图 1", hint: "网站首页大图 + PDF 封面", accept: RASTER_ACCEPT },
  { role: "advantage", label: "核心图 2", hint: "PDF「项目优势」页配图", accept: RASTER_ACCEPT },
  { role: "eligibility", label: "核心图 3", hint: "PDF「申请条件」页配图", accept: RASTER_ACCEPT },
  { role: "process", label: "核心图 4", hint: "PDF「申请流程」页配图", accept: RASTER_ACCEPT },
  {
    role: "scenery",
    label: "风光掠影",
    hint: "首页概览区 + PDF 背景图（不上传则使用核心图 1）",
    accept: RASTER_ACCEPT,
  },
  { role: "flag", label: "国旗", hint: "网站徽章 + PDF 国旗标识", accept: RASTER_ACCEPT },
  {
    role: "headerFlag",
    label: "导航国旗 (PNG)",
    hint: "网站顶部导航栏国旗图标，仅支持 PNG",
    accept: "image/png",
  },
];

function formatBytes(bytes: number): string {
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
}

export default function ImageManager(props: {
  programId: string;
  images: ProgramImage[];
  onChange: (images: ProgramImage[]) => void;
}) {
  const { programId, images, onChange } = props;
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [slotUploading, setSlotUploading] = useState<ProgramImage["role"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function tooLarge(file: File): string | null {
    if (file.size > MAX_IMAGE_BYTES) {
      return `文件过大（${formatBytes(file.size)}），需小于 ${formatBytes(MAX_IMAGE_BYTES)}`;
    }
    return null;
  }

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    const sizeError = tooLarge(file);
    if (sizeError) {
      setError(sizeError);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const image = await uploadProgramImage(programId, file, GALLERY_ROLE, caption || undefined);
      onChange([...images, image]);
      setCaption("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "上传失败");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDelete(image: ProgramImage) {
    if (!window.confirm("确定要删除这张图片吗？")) return;
    await deleteProgramImage(programId, image.id);
    onChange(images.filter((i) => i.id !== image.id));
  }

  async function handleSlotFileSelected(
    slot: (typeof CORE_SLOTS)[number],
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    const sizeError = tooLarge(file);
    if (sizeError) {
      setError(`${slot.label}：${sizeError}`);
      return;
    }
    setSlotUploading(slot.role);
    try {
      const image = await uploadProgramImage(programId, file, slot.role);
      // The backend already deletes any previous image(s) in this role as
      // part of the upload -- drop them from local state the same way
      // rather than re-deleting (the rows are already gone server-side).
      onChange([...images.filter((i) => i.role !== slot.role), image]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "上传失败");
    } finally {
      setSlotUploading(null);
    }
  }

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-6">
        {CORE_SLOTS.map((slot) => {
          const image = images.find((i) => i.role === slot.role);
          const isUploading = slotUploading === slot.role;
          return (
            <div key={slot.role} className="border border-fourth-light rounded-lg overflow-hidden">
              <div className="relative aspect-video bg-c-gray flex items-center justify-center">
                {image ? (
                  <img
                    src={image.url}
                    alt={slot.label}
                    className={`w-full h-full ${slot.role === "headerFlag" ? "object-contain p-3" : "object-cover"}`}
                  />
                ) : (
                  <span className="text-xs text-secondary-light">未上传</span>
                )}
                {image && (
                  <button
                    type="button"
                    onClick={() => handleDelete(image)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-secondary-dark/80 text-white text-xs"
                    title="删除"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="p-2.5">
                <p className="text-sm font-medium text-secondary-dark">{slot.label}</p>
                <p className="text-xs text-secondary-light mb-2">{slot.hint}</p>
                <input
                  type="file"
                  accept={slot.accept}
                  onChange={(e) => handleSlotFileSelected(slot, e)}
                  disabled={isUploading}
                  className="block text-xs w-full bg-white"
                />
                {isUploading && <span className="text-xs text-secondary-light">上传中…</span>}
                {!isUploading && image?.sizeBytes != null && (
                  <span className="text-xs text-secondary-light">{formatBytes(image.sizeBytes)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
        {images
          .filter((image) => !CORE_SLOTS.some((slot) => slot.role === image.role))
          .map((image) => (
            <div key={image.id} className="relative group border border-fourth-light rounded-lg overflow-hidden">
              <img src={image.url} alt={image.caption ?? "图集"} className="w-full aspect-square object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-secondary-dark/80 text-white text-[11px] px-2 py-1 flex items-center justify-between gap-2">
                <span className="truncate">{image.caption || "图集"}</span>
                {image.sizeBytes != null && (
                  <span className="shrink-0 text-white/70">{formatBytes(image.sizeBytes)}</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleDelete(image)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-secondary-dark/80 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                title="删除"
              >
                ✕
              </button>
            </div>
          ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 border-t border-fourth-light pt-4">
        <p className="text-xs text-secondary-light w-full -mb-1">
          添加图集图片（gallery），需小于 {formatBytes(MAX_IMAGE_BYTES)}
        </p>
        <label className="text-sm flex-1 min-w-[160px]">
          <span className="block text-secondary-dark mb-1">说明文字（可选）</span>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            className="w-full border border-fourth-light rounded-lg px-2 py-1.5 text-sm outline-none focus:border-gold bg-white"
          />
        </label>
        <label className="text-sm">
          <input
            ref={fileInputRef}
            type="file"
            accept={RASTER_ACCEPT}
            onChange={handleFileSelected}
            disabled={uploading}
            className="block text-xs"
          />
        </label>
        {uploading && <span className="text-xs text-secondary-light">上传中…</span>}
      </div>
      {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
    </div>
  );
}
